import {Box, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import './RelevancyDocsPanel.scss';
import {t} from 'i18next';
import {useGetAIReviewRelevancyDocSummary} from '../../../../../hooks/ai/review/useReview';
import {Error, Warning} from '@mui/icons-material';
import ArrBanner from '../../../../common/arr-banner/ArrBanner';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../shared/constants';

const EMPTY_DISPLAY_VALUE = '-';

const getDisplayValue = (value: unknown) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return EMPTY_DISPLAY_VALUE;
  }

  return String(value);
};

interface IRelevancyDocsPanel {
  isRelevancyDocsPanelOpen: boolean;
  reviewjobid?: number | string | null;
  reviewJobSubmittedId?: number | string | null;
  docId?: number | string | null;
  /** Legacy props remain accepted for callers compiled outside this flow. */
  jobId?: number | string;
  selectedDoc?: unknown;
  onClose?: () => void;
}

const RelevancyDocsPanel = ({
  isRelevancyDocsPanelOpen,
  reviewjobid,
  reviewJobSubmittedId,
  docId,
}: IRelevancyDocsPanel) => {
  const detailRequest = {
    reviewjobid: reviewjobid ?? '',
    reviewJobSubmittedId: reviewJobSubmittedId ?? '',
  };
  const {
    data: docSummary,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAIReviewRelevancyDocSummary(
    detailRequest,
    isRelevancyDocsPanelOpen
  );
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);

  useEffect(() => {
    if (!isRelevancyDocsPanelOpen || !isError) {
      setIsErrorToastOpen(false);
      return;
    }

    setIsErrorToastOpen(true);
  }, [isError, isRelevancyDocsPanelOpen, reviewJobSubmittedId, reviewjobid]);

  const handleRetry = async () => {
    setIsErrorToastOpen(false);
    try {
      const result = await refetch();
      if (result.isError) {
        setIsErrorToastOpen(true);
      }
    } catch {
      setIsErrorToastOpen(true);
    }
  };

  const isDetailLoading = isLoading || isFetching;
  const shouldShowError = !isDetailLoading && (isError || !docSummary);
  const selectedDocId = docId;
  const isFlaggedForHumanReview = docSummary?.isFlaggedForHumanReview === true;
  const isExcluded = docSummary?.isExcluded === true;
  const tags = (docSummary?.tags ?? []).filter((tag) => tag.trim() !== '');
  const humanReviewTags = (docSummary?.humanReviewTags ?? []).filter(
    (tag) => tag.trim() !== ''
  );

  return (
    <>
      <Box
        className="revelancy-box"
        data-testid="relevancy-docs-panel"
        data-open={String(isRelevancyDocsPanelOpen)}
        data-job-id={String(reviewjobid ?? '')}
        data-doc-id={String(selectedDocId ?? '')}
      >
        {isDetailLoading ? (
          <Box
            className="relevancy-docs-panel__state"
            data-testid="relevancy-docs-panel-loading"
          >
            <ArrLoadingSpinner />
          </Box>
        ) : shouldShowError ? (
          <Box
            className="relevancy-docs-panel__error"
            data-testid="relevancy-docs-panel-error"
            role="alert"
            aria-live="assertive"
          >
            <Typography
              component="h3"
              className="typo-text subhead1_size text-headings relevancy-docs-panel__error-title"
              data-testid="relevancy-docs-panel-error-title"
            >
              {t('ai.review.jobResult.relevancyDocsApiFailureTitle')}
            </Typography>

            <ArrButton
              variant="primary"
              size="small"
              dataTestId="relevancy-docs-panel-error-retry-button"
              onClick={handleRetry}
            >
              {t('ai.review.jobResult.relevancyDocsApiFailureRetry')}
            </ArrButton>
          </Box>
        ) : (
          <Box className="docs-relevenacy-container">
            {/* Header */}
            <Box className="header">
              <Typography
                className="typo-text subhead4_size"
                tabIndex={0}
                data-testid="relveancy-panel-documentSummary"
              >
                {t('ai.review.jobResult.summaryDocs.documentSummary')}
              </Typography>
            </Box>
            <Box className="box-section-top">
              <Box className="top-section">
                <Typography
                  className="typo-text label3_size top-section-title"
                  tabIndex={0}
                  data-testid="relveancy-panel-relavancy"
                >
                  {t('ai.review.jobResult.summaryDocs.relevancy')}
                </Typography>
                <Typography
                  className="typo-text body3_size top-section-text"
                  tabIndex={1}
                  data-testid="relevancy-panel-relevancyText"
                >
                  {getDisplayValue(docSummary?.responsiveness)}
                </Typography>
              </Box>
              <Box className="top-section">
                <Typography
                  className="typo-text label3_size top-section-title"
                  tabIndex={2}
                  data-testid="relevancy-panel-confidence"
                >
                  {t('ai.review.jobResult.summaryDocs.confidence')}
                </Typography>
                <Typography
                  className="typo-text body3_size top-section-text"
                  tabIndex={3}
                  data-testid="relevancy-panel-confidenceText"
                >
                  {getDisplayValue(docSummary?.confidence)}
                </Typography>
              </Box>
            </Box>
            <Box className="top-section">
              <Typography
                className="typo-text label3_size top-section-title"
                tabIndex={6}
                data-testid="relveancy-panel-reason"
              >
                {t('ai.review.jobResult.summaryDocs.reason')}
              </Typography>
              <Typography
                className="typo-text body3_size top-section-text"
                tabIndex={7}
                data-testid="relevancy-panel-reasonText"
              >
                {getDisplayValue(docSummary?.reason)}
              </Typography>
            </Box>
            <Box className="top-section">
              <Typography
                className="typo-text label3_size top-section-title"
                tabIndex={8}
                data-testid="relevancy-panel-tags"
              >
                {t('ai.review.jobResult.summaryDocs.tags')}
              </Typography>
              <Box className="relevancy-tags-group">
                {tags.length > 0 ? (
                  tags.map((tag, index) => (
                    <Typography
                      key={`${tag}-${index}`}
                      className="typo-text label3_size top-section-text-tag"
                      tabIndex={9}
                      data-testid="relevancy-panel-tagsText"
                    >
                      {tag}
                    </Typography>
                  ))
                ) : (
                  <Typography
                    className="typo-text body3_size top-section-text"
                    tabIndex={9}
                    data-testid="relevancy-panel-tagsText"
                  >
                    {EMPTY_DISPLAY_VALUE}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box className="banner-section">
              {isExcluded && (
                <ArrBanner
                  type="persistent"
                  variant="error"
                  startIcon={<Error sx={{alignSelf: 'flex-start'}} />}
                  className="coding-conflicts-banner inactive-index-error-banner"
                  bannerContent={
                    <Box className="font-color-ai-review">
                      <Box>
                        <Box>
                          <Typography
                            className="typo-text subhead5_size"
                            tabIndex={0}
                            data-testid="relevancy-panel-criticalTitle"
                          >
                            {t('ai.review.jobResult.summaryDocs.excluded')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Typography
                          className="excluded-details typo-text body4_size"
                          tabIndex={0}
                          data-testid="relevancy-panel-criticalText"
                        >
                          {getDisplayValue(docSummary?.excludedDetails)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  dataTestId="critical-form-banner"
                />
              )}

              {isFlaggedForHumanReview && (
                <ArrBanner
                  type="persistent"
                  variant="warning"
                  startIcon={<Warning sx={{alignSelf: 'flex-start'}} />}
                  bannerContent={
                    <Box className="font-color-ai-review">
                      <Box className="flex-display">
                        <Box>
                          <Typography
                            className="typo-text subhead5_size font-color-ai-review flag-review-title"
                            tabIndex={0}
                            data-testid="relevancy-panel-flagwarning"
                          >
                            {t(
                              'ai.review.jobResult.summaryDocs.flaggedForHumanReview'
                            )}
                          </Typography>
                        </Box>
                        {humanReviewTags.length > 0 && (
                          <Box className="warning-label-group">
                            {humanReviewTags.map((tag, index) => (
                              <Box
                                className="warning-label"
                                key={`${tag}-${index}`}
                              >
                                <Typography
                                  className="typo-text label4_size label_color"
                                  tabIndex={0}
                                  data-testid="relevancy-panel-flagSecondWarning"
                                >
                                  {tag}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <Typography
                          className="typo-text body4_size flag-review-details"
                          tabIndex={0}
                          data-testid="relevancy-panel-flagWarningText"
                        >
                          {docSummary?.humanReviewDetails}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  className="coding-conflicts-banner inactive-index-warning-banner"
                  dataTestId="warning-form-banner"
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {isErrorToastOpen && (
        <ArrToastNotification
          message={t('ai.review.jobResult.relevancyDocsApiFailureToast')}
          status={[ToastType.Error]}
          onClose={() => setIsErrorToastOpen(false)}
        />
      )}
    </>
  );
};

export default RelevancyDocsPanel;
